import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { commitMutation, graphql } from "react-relay";
import { useLocation } from "react-router";

import { faEdit, faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faEllipsisV, faFileAlt } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCaretDown, faCaretUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import moment from "moment";

import { decodeNoteLocation } from "~/actionItems/locationState";
import { prompt } from "~/lib/prompts";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import store from "~/lib/store";
import { inCalendarMode } from "~/lib/utils";
import PrivateNote, { type PrivateNoteRef } from "~/meetings/SeriesSidePanel/PrivateNotes/PrivateNote";
import { formatDate, sortByLinkedNote } from "~/meetings/SeriesSidePanel/PrivateNotes/utils";
import { environment } from "~/models";
import { Button, toast, Tooltip } from "~/ui";
import { IconLinkButton } from "~/ui/button/IconButton";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import Menu from "~/ui/menu";

import { SidePanelItem } from "../SidePanelItem";

import type { PrivateNotesPanelCreatePrivateNoteMutation } from "~/__generated__/PrivateNotesPanelCreatePrivateNoteMutation.graphql";
import type {
	PrivateNotesPanelListQuery,
	PrivateNotesPanelListQuery$data,
} from "~/__generated__/PrivateNotesPanelListQuery.graphql";

import sideBarStyles from "../SeriesSidePanel.module.css";
import privateNoteStyles from "./PrivateNote.module.css";
import styles from "./PrivateNotes.module.css";

const privateNoteListQuery = graphql`
	query PrivateNotesPanelListQuery($id: ID!) {
		viewer {
			uxSimplificationEnabled
			account {
				id
				billingAccount {
					hasAccessToDuedates: hasAccessToFeature(feature: duedates)
					hasAccessToEmbeds: hasAccessToFeature(feature: embeds)
					hasAccessToObjectives: hasAccessToFeature(feature: objectives)
					hasAccessToMultipleAssignees: hasAccessToFeature(feature: multiple_assignees)
					featureAccess
				}
				allowCompleteByAny
				tags {
					__typename
					id
					label
					color
				}
			}
		}
		stream: node(id: $id) {
			... on NoteStream {
				privateNotes {
					id
					title
					linkedNote {
						id
						eventStart
						createdAt
						url
					}
					note {
						id
					}
				}
			}
		}
	}
`;

const createPrivateNoteMutation = graphql`
	mutation PrivateNotesPanelCreatePrivateNoteMutation($streamId: ID!, $noteId: ID) {
		createPrivateNote(streamId: $streamId, noteId: $noteId) {
			privateNote {
				...PrivateNoteFragment @relay(mask: false)
			}
			stream {
				privateNotes {
					id
					title
					linkedNote {
						id
						eventStart
						createdAt
						url
					}
				}
			}
		}
	}
`;

interface PrivateNoteItemProps {
	privateNote: NonNullable<NonNullable<PrivateNotesPanelListQuery$data["stream"]>["privateNotes"]>[0];
	onSelect(id: string): void;
}

function PrivateNoteItem({ privateNote, onSelect }: PrivateNoteItemProps) {
	const onClick = useCallback(() => {
		onSelect(privateNote.id);
		dispatchAnalyticsTrack("Selected a private note from the switcher", {
			feature: "private-notes",
		});
	}, [privateNote.id, onSelect]);

	const date = useMemo(() => {
		if (!privateNote.linkedNote) return;
		if (privateNote.linkedNote.eventStart) return formatDate(privateNote.linkedNote.eventStart);
		if (privateNote.linkedNote.createdAt) return formatDate(privateNote.linkedNote.createdAt);
		return;
	}, [privateNote.linkedNote]);

	return (
		<Menu.Item onSelect={onClick} className={styles.privateNoteItem}>
			<span className={styles.privateNoteTitle}>{privateNote.title || "Untitled"}</span>
			{date && <span className={styles.linkedNote}>{date}</span>}
		</Menu.Item>
	);
}

const privateNotesStore = store.namespace("privateNotes");
const LAST_PRIVATE_NOTE_OPENED_PREFIX = "lastPrivateNoteOpened_";

interface PrivateNotesProps {
	streamId: string;
	noteId: string | null;
	onClose?: () => void;
}

export interface PrivateNotesLocationState {
	fromLinkedNoteIcon?: boolean;
}

export default function PrivateNotes({ streamId, noteId, onClose }: PrivateNotesProps) {
	const location = useLocation<PrivateNotesLocationState>();
	// TODO: preload this query
	const data = useLazyLoadQuery<PrivateNotesPanelListQuery>(privateNoteListQuery, { id: streamId }, {});
	const { viewer } = data;
	const privateNotes = data.stream?.privateNotes;
	const [isVisible, setIsVisible] = useState<boolean>(false);

	const latestPrivateNote = useMemo(() => {
		if (!privateNotes || privateNotes.length === 0) return null;
		let latestPrivateNote = privateNotes[0];
		for (const privateNote of privateNotes) {
			const { linkedNote } = privateNote;
			if (!linkedNote) continue;
			const privateNoteDate = linkedNote.eventStart || linkedNote.createdAt;
			const currentPrivateNoteDate =
				latestPrivateNote.linkedNote?.eventStart || latestPrivateNote.linkedNote?.createdAt;
			if (!privateNoteDate || !currentPrivateNoteDate) continue;
			if (privateNoteDate > currentPrivateNoteDate) {
				latestPrivateNote = privateNote;
			}
		}
		return latestPrivateNote;
	}, [privateNotes]);

	const selectedNoteFromLocation = useMemo((): { privateNoteId: string; blockKey?: string } | undefined => {
		if (!location.hash) return;
		const locationParams = decodeNoteLocation(location.hash.slice(1));
		if (!locationParams?.noteId || !locationParams.isPrivateNote) return;
		const foundPrivateNote = privateNotes?.find(privateNote => privateNote.note.id === locationParams.noteId);
		if (!foundPrivateNote) return;
		return {
			privateNoteId: foundPrivateNote.id,
			blockKey: locationParams.blockKey,
		};
	}, [privateNotes, location.hash]);

	const fromLinkedNoteIcon = location.state?.fromLinkedNoteIcon;

	const getDefaultSelectedPrivateNote = useCallback(() => {
		const lastPrivateNoteOpenedKey = LAST_PRIVATE_NOTE_OPENED_PREFIX + streamId;
		if (privateNotesStore.has(lastPrivateNoteOpenedKey)) {
			const storedPrivateNoteId = privateNotesStore.get(lastPrivateNoteOpenedKey);
			if (privateNotes?.some(privateNote => privateNote.id === storedPrivateNoteId)) {
				return storedPrivateNoteId;
			}
		}
		if (selectedNoteFromLocation) return selectedNoteFromLocation.privateNoteId;
		if (latestPrivateNote) return latestPrivateNote.id;
		return null;
	}, [latestPrivateNote, privateNotes, selectedNoteFromLocation, streamId]);

	const [selectedPrivateNoteId, setSelectedPrivateNoteId] = useState<string | undefined>(getDefaultSelectedPrivateNote);

	const selectedPrivateNote = useMemo(() => {
		return privateNotes?.find(privateNote => privateNote?.id === selectedPrivateNoteId);
	}, [privateNotes, selectedPrivateNoteId]);

	// If we don't have a private note matching the selected ID, reset the selected ID
	// to the default. (e.g. if it was deleted, or if we navigated to a different stream)
	useEffect(() => {
		if (selectedPrivateNoteId && !selectedPrivateNote) {
			setSelectedPrivateNoteId(getDefaultSelectedPrivateNote());
		}
	}, [getDefaultSelectedPrivateNote, selectedPrivateNote, selectedPrivateNoteId]);

	const [initiallyHighlightedBlockKey, setInitiallyHighlightedBlockKey] = useState<string | undefined>();
	const [isMenuOpen, _setIsMenuOpen] = useState(false);
	const [isOptionsMenuOpen, setIsOptionsMenuOpen] = useState(false);
	const privateNoteRef = useRef<PrivateNoteRef>(null);

	const setOpen = useCallback((open: boolean) => {
		_setIsMenuOpen(() => open);
		dispatchAnalyticsTrack("Opened private note switcher", { feature: "private-notes" });
	}, []);

	const toggleOptionsOpen = useCallback(() => setIsOptionsMenuOpen(open => !open), []);

	const changeSelectedPrivateNote = useCallback((privateNoteId: string, highlightedBlockKey?: string) => {
		setInitiallyHighlightedBlockKey(highlightedBlockKey);
		setSelectedPrivateNoteId(privateNoteId);
	}, []);

	// Store the last private note opened in local storage
	useEffect(() => {
		if (!selectedPrivateNote) return;
		const lastPrivateNoteOpenedKey = LAST_PRIVATE_NOTE_OPENED_PREFIX + streamId;
		privateNotesStore.set(lastPrivateNoteOpenedKey, selectedPrivateNote.id);
	}, [selectedPrivateNote, streamId]);

	const onCreatePrivateNote = useCallback(() => {
		commitMutation<PrivateNotesPanelCreatePrivateNoteMutation>(environment, {
			mutation: createPrivateNoteMutation,
			variables: {
				noteId: noteId || null,
				streamId,
			},
			onCompleted: response => {
				if (!response.createPrivateNote) {
					toast.error("Failed to create private note");
				}
				const privateNote = response.createPrivateNote?.privateNote;
				if (!privateNote) return;
				setSelectedPrivateNoteId(privateNote.id);
				setIsVisible(true);
			},
			onError: () => {
				toast.error("Failed to create private note");
			},
		});
		dispatchAnalyticsTrack("Create private note", { feature: "private-notes" });
	}, [noteId, streamId]);

	const onRename = useCallback(() => {
		if (!privateNoteRef.current) return;
		prompt("", {
			initialInputValue: privateNoteRef.current.title ?? "",
			title: "Rename private note",
			inputPlaceholder: "Name this private note… (max 255 chars)",
			yesText: "Rename",
		}).then(val => {
			if (!val || val == privateNoteRef.current?.title) {
				return;
			}
			if (typeof val === "string" && val.length > 255) {
				toast.error("Private note name must be 255 characters or less");
				return;
			}
			privateNoteRef.current?.onUpdateTitle(val as string, true);
		});
	}, []);

	const onDelete = useCallback(async () => {
		if (!privateNotes || privateNotes?.length === 1) return;
		const newSelectedPrivateNoteId =
			privateNotes[0].id !== selectedPrivateNoteId ? privateNotes[0].id : privateNotes[1].id;
		const deleted = await privateNoteRef.current?.onDelete();
		if (!deleted) return;
		setSelectedPrivateNoteId(newSelectedPrivateNoteId);
	}, [privateNotes, selectedPrivateNoteId]);

	// Create a private note if there are none
	useEffect(() => {
		if (privateNotes?.length === 0) {
			onCreatePrivateNote();
		}
	}, [privateNotes?.length, onCreatePrivateNote]);

	// If our URL points to a specific private note, select it
	useEffect(() => {
		if (!selectedNoteFromLocation) return;
		changeSelectedPrivateNote(selectedNoteFromLocation.privateNoteId, selectedNoteFromLocation.blockKey);
		setIsVisible(true);
	}, [selectedNoteFromLocation, changeSelectedPrivateNote, setIsVisible]);

	// If we're coming from a click on the linked note icon, set isVisible to true
	useEffect(() => {
		if (fromLinkedNoteIcon) {
			setIsVisible(true);
		}
	}, [fromLinkedNoteIcon, setIsVisible]);

	const onClickLinkedNoteIcon = useCallback(() => {
		dispatchAnalyticsTrack("Click linked note icon", { feature: "private-notes", from: "side-bar" });
	}, []);

	const privateNotesList = useMemo(() => {
		if (!data.stream?.privateNotes) return [];
		const privateNotes = [...data.stream.privateNotes];
		privateNotes.sort(sortByLinkedNote);
		return privateNotes;
	}, [data.stream?.privateNotes]);

	const noteLinkIcon = useMemo(() => {
		if (!selectedPrivateNote || !selectedPrivateNote.linkedNote?.url) return null;
		const startDate = moment(
			selectedPrivateNote.linkedNote.eventStart || selectedPrivateNote.linkedNote.createdAt || "",
		);
		const noteDateString =
			startDate.year() === moment().year() ? startDate.format("MMM D") : startDate.format("MMM D YYYY");
		return (
			<Tooltip
				placement="top"
				content={
					<span className={privateNoteStyles.linkedNoteTooltip}>Bring linked note to view {noteDateString}</span>
				}
			>
				<IconLinkButton
					variant="tertiary"
					icon={faFileAlt}
					to={{
						pathname:
							viewer.uxSimplificationEnabled && inCalendarMode
								? `/companion-mode/${selectedPrivateNote.linkedNote.id}/notes`
								: selectedPrivateNote.linkedNote.url,

						state: { fromLinkedNoteIcon: true },
					}}
					className={styles.linkedNoteIcon}
					onClick={onClickLinkedNoteIcon}
				/>
			</Tooltip>
		);
	}, [onClickLinkedNoteIcon, selectedPrivateNote, viewer.uxSimplificationEnabled]);

	const privateNoteToRender = useMemo(() => {
		if (!selectedPrivateNoteId) return <></>;
		return (
			<React.Suspense
				fallback={
					<SidePanelItem.Body className={sideBarStyles.loadingBody}>
						<LoadingSpinner dimmer={false} inline className={styles.spinner} />
					</SidePanelItem.Body>
				}
			>
				<SidePanelItem.Body>
					<PrivateNote
						id={selectedPrivateNoteId}
						isVisible={isVisible}
						setIsVisible={setIsVisible}
						viewer={viewer}
						ref={privateNoteRef}
						initiallyHighlightedBlockKey={initiallyHighlightedBlockKey}
					/>
				</SidePanelItem.Body>
			</React.Suspense>
		);
	}, [selectedPrivateNoteId, isVisible, viewer, initiallyHighlightedBlockKey]);

	return (
		<SidePanelItem>
			<SidePanelItem.Header
				headerTitle={<SidePanelItem.Title>Private Notes</SidePanelItem.Title>}
				createAction={onCreatePrivateNote}
				actionsMenu={
					<Menu open={isOptionsMenuOpen} onOpenChange={toggleOptionsOpen}>
						<Menu.Trigger>
							<SidePanelItem.HeaderIconButton icon={faEllipsisV} />
						</Menu.Trigger>
						<Menu.Content align="end">
							<Menu.Item onSelect={onRename} icon={faEdit}>
								Rename…
							</Menu.Item>
							<Menu.Item onSelect={onDelete} icon={faTrashCan} destructive disabled={privateNotes?.length === 1}>
								Delete
							</Menu.Item>
						</Menu.Content>
					</Menu>
				}
				onClose={onClose}
			>
				<div className={styles.privateNotesMenu}>
					<Menu open={isMenuOpen} onOpenChange={setOpen}>
						<Menu.Trigger disabled={!isVisible}>
							<Button
								variant="secondary"
								title={selectedPrivateNote?.title ? selectedPrivateNote.title : ""}
								className={cx(styles.dropdownButton, { [styles.active]: isMenuOpen })}
							>
								<div>{selectedPrivateNote ? selectedPrivateNote.title : ""}</div>
								<FontAwesomeIcon className={styles.dropdownIcon} icon={isMenuOpen ? faCaretUp : faCaretDown} />
							</Button>
						</Menu.Trigger>
						<Menu.Content align="start" className={styles.menuContent}>
							{privateNotesList.map(privateNote => (
								<PrivateNoteItem key={privateNote.id} privateNote={privateNote} onSelect={changeSelectedPrivateNote} />
							))}
						</Menu.Content>
					</Menu>
					{noteLinkIcon}
				</div>
			</SidePanelItem.Header>

			{privateNoteToRender}
		</SidePanelItem>
	);
}
